import React, { useState, useContext, useEffect } from 'react';

import {
	Typography,
	Stack,
	Paper,
	Grid,
	TableContainer,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	TextField,
	Button
} from '@mui/material';
import moment from 'moment';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import { DataContext } from '../../../../context/DataContext';
import {
	getDataLiquidacionAsync,
	getVacacionesAsync,
	liquidarEmpleadoAsync
} from '../../../../api/Employees';
import { getToken } from '../../../../api/Account';
import { simpleMessage } from '../../../../helpers/Helpers';
import { ConfirmDialog } from '../../../../components/base';
import { formatearMonto } from '../../../../helpers/formato';
import { formatearAntiguedadServidor } from '../comunes';

// Conceptos de las filas con reglas propias. Son el texto exacto con el que el servidor arma cada fila
// (EmployeeHelper.GetDataLiquidationAsync): las filas se eligen por su concepto y nunca por su posición,
// porque cambian (sin nómina abierta no hay salario ni bono, y el servidor puede agregar otras al final).
// - BONO POR RESULTADOS: el monto manual. Llega en 0 y es la ÚNICA fila editable.
// - BONOS PENDIENTES (al final, no editable): suma de los bonos ya registrados. No necesita regla propia.
// - PRESTAMOS PENDIENTES (al final, no editable): llega con Valor = -(saldo total de préstamos) y en
//   pantalla se descuenta con tope (ver `conTopeDePrestamos`).
// Las filas nuevas pueden no venir: si faltan, no pasa nada.
export const DETALLE_BONO = 'BONO POR RESULTADOS';
export const DETALLE_PRESTAMOS = 'PRESTAMOS PENDIENTES';

// A 2 decimales con redondeo "AwayFromZero" (como el servidor: cada fila a centavos y luego se suma).
// Normaliza el -0 a 0, que se vería como "-C$0.00".
export const aCentavos = (monto) => {
	const redondeado = (Math.sign(monto) * Math.round(Math.abs(monto) * 100 + 1e-7)) / 100;
	return redondeado === 0 ? 0 : redondeado;
};

// Devuelve las filas, cada una a centavos, con el descuento de préstamos con tope:
// -min(saldo, max(0, suma de las demás filas)). La liquidación nunca queda negativa. No muta las filas
// recibidas. El servidor igual lo recalcula con la misma regla.
export const conTopeDePrestamos = (filas) => {
	const redondeadas = filas.map((fila) => ({ ...fila, valor: aCentavos(fila.valor) }));
	const otras = aCentavos(
		redondeadas
			.filter((fila) => fila.detalle !== DETALLE_PRESTAMOS)
			.reduce((suma, fila) => suma + fila.valor, 0)
	);
	return redondeadas.map((fila) => {
		if (fila.detalle !== DETALLE_PRESTAMOS) return fila;
		const saldo = Math.max(0, -fila.valor);
		return { ...fila, valor: -aCentavos(Math.min(saldo, Math.max(0, otras))) || 0 };
	});
};

const LiquidateEmployee = ({
	dataEmployee,
	setShowModal,
	reload,
	setReload,
	onBonoEscrito
}) => {
	// Liquidar es irreversible: el botón solo abre la confirmación y `liquidarEmployee` se llama
	// una sola vez, al confirmar, con `procesando` mientras dura.
	const [confirmando, setConfirmando] = useState(false);
	const [procesando, setProcesando] = useState(false);

	const [histoyVac, setHistoryBac] = useState([]);
	const [dataLiquidacion, setDataLiquidacion] = useState([]);
	// El campo "Bono" es el monto manual: arranca en 0 (como la fila que llega del servidor) y vuelve a 0
	// cada vez que se piden las filas, p. ej. al cambiar la fecha. Es texto: lo que escribe quien liquida.
	const [montoBono, setMontoBono] = useState('0');
	const [errorBono, setErrorBono] = useState('');
	const [total, setTotal] = useState(0);
	// const [topSalario, setTopSalario] = useState(0);

	const [fechaLiquidacion, setFechaLiquidacion] = useState(new Date());

	const {
		fullName,
		puesto,
		address,
		identification,
		tipoContrato,
		fechaInicioContrato,
		inssNumber,
		salario,
		fechaUltimoPago
	} = dataEmployee;
	const { setIsLoading, setIsUnautorized } = useContext(DataContext);

	useEffect(() => {
		getEmployee();
		// getBestSalario(fechaLiquidacion);
		getDataLiquidacion(fechaLiquidacion);
	}, [fechaLiquidacion]);

	// Avisa al diálogo de si ya se escribió un bono, para que un clic fuera no lo pierda.
	useEffect(() => {
		if (onBonoEscrito) onBonoEscrito(String(montoBono) !== '0' && String(montoBono) !== '');
	}, [montoBono]);

	const token = getToken();

	const getEmployee = async () => {
		setIsLoading(true);
		const result = await getVacacionesAsync(token, dataEmployee.id);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}
		setHistoryBac(result.data.result);
		setIsLoading(false);
	};

	const getDataLiquidacion = async (fechaLiquidacion) => {
		setIsLoading(true);
		const data = {
			employeeId: dataEmployee.id,
			fechaLiquidacion: new Date(fechaLiquidacion).toISOString()
		};
		const result = await getDataLiquidacionAsync(token, data);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}
		setDataLiquidacion(result.data.result);
		// Filas nuevas: el bono manual vuelve a 0, igual que su fila.
		setMontoBono('0');
		setErrorBono('');
		setIsLoading(false);
	};

	// const getBestSalario = async () => {
	// 	setIsLoading(true);
	// 	const result = await getEmployeeBestSalarioAsync(token, dataEmployee.id);
	// 	if (!result.statusResponse) {
	// 		setIsLoading(false);
	// 		if (result.error === 'Unauthorized') {
	// 			setIsUnautorized(true);
	// 			return;
	// 		}
	// 		simpleMessage(result.error, 'error');
	// 		return;
	// 	}
	// 	setTopSalario(result.data.result);
	// 	setIsLoading(false);
	// };

	// El valor del bono se cambia en una copia de las filas: el estado no se muta.
	const setMontoBonoFunc = (value) => {
		if (/^\d*\.?\d*$/.test(value.toString()) || value === '') {
			setMontoBono(value);
			const valor = parseFloat(value === '' ? 0 : value);
			// Un "." solo no es un número: se avisa junto al campo y las filas y el total no cambian
			// (así el payload nunca lleva NaN, que viajaría como null).
			if (Number.isNaN(valor)) {
				setErrorBono('Escribe un monto válido, por ejemplo 250 o 250.50');
				return;
			}
			setErrorBono('');
			setDataLiquidacion((actual) => {
				if (!actual.liquidacionDetails) return actual;
				return {
					...actual,
					liquidacionDetails: actual.liquidacionDetails.map((fila) =>
						fila.detalle === DETALLE_BONO ? { ...fila, valor } : fila
					)
				};
			});
		}
	};

	// Filas que se ven y se envían: las del servidor con el tope de préstamos recalculado en vivo.
	// Mientras no hay datos (el estado inicial es []), no hay filas.
	const filasMostradas = dataLiquidacion.liquidacionDetails
		? conTopeDePrestamos(dataLiquidacion.liquidacionDetails)
		: null;

	// Cada fila a centavos y luego la suma, también a centavos (sin ruido de coma flotante ni -0).
	const getTotal = (detalle) => {
		let result = 0;
		detalle.map((item) => {
			result += aCentavos(item.valor);
		});

		return aCentavos(result);
	};

	const liquidarEmployee = async () => {
		const datos = {
			employeeId: dataEmployee.id,
			antiguedad: histoyVac.antiguedad,
			vacaionesDisponibles: histoyVac.saldoVac,
			salarioBasico: salario,
			fechaLiquidacion: new Date(fechaLiquidacion).toISOString(),
			// Las filas como se ven en pantalla: el descuento de préstamos ya con su tope.
			liquidacionDetails: filasMostradas
		};

		setIsLoading(true);
		const result = await liquidarEmpleadoAsync(token, datos);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}
		setIsLoading(false);
		simpleMessage('Empleado Liquidado...!', 'success');
		setReload(!reload);
		setShowModal(false);
	};

	// El botón solo abre la confirmación; `liquidarEmployee` se llama al confirmar.
	const confirmarLiquidar = async () => {
		setProcesando(true);
		try {
			await liquidarEmployee();
		} finally {
			setProcesando(false);
			setConfirmando(false);
		}
	};

	const totalLiquidacion = filasMostradas === null ? null : getTotal(filasMostradas);

	const dato = (etiqueta, valor) => (
		<>
			<Grid item xs={6}>
				<Typography
					textAlign={'left'}
					sx={{ color: 'text.secondary', fontWeight: 700 }}
				>
					{etiqueta}
				</Typography>
			</Grid>
			<Grid item xs={6}>
				<Typography textAlign="left" sx={{ overflowWrap: 'anywhere' }}>
					{valor}
				</Typography>
			</Grid>
		</>
	);

	return (
		<div>
			<Paper sx={{ p: { xs: 2, sm: 3 } }}>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					justifyContent="space-between"
					alignItems={{ xs: 'stretch', sm: 'center' }}
					spacing={2}
				>
					<Typography
						component="h3"
						variant="h6"
						textAlign={'left'}
						sx={{ fontWeight: 700, color: 'primary.main' }}
					>
						Datos del empleado
					</Typography>
					<Stack direction={'row'} alignItems="center" spacing={1}>
						<DesktopDatePicker
							closeOnSelect
							label="Fecha de liquidación"
							inputFormat="DD/MM/YYYY"
							value={fechaLiquidacion}
							onChange={(newValue) => setFechaLiquidacion(newValue)}
							renderInput={(params) => <TextField {...params} />}
						/>
					</Stack>
				</Stack>

				<Grid container spacing={1} sx={{ mt: 1 }}>
					{dato('EMPLEADO:', `${inssNumber} - ${fullName}`)}
					{dato('CÉDULA:', identification)}
					{dato('FECHA DE INGRESO:', moment(fechaInicioContrato).format('L'))}
					{dato('ANTIGÜEDAD:', formatearAntiguedadServidor(histoyVac.antiguedad))}
					{dato('VACACIONES DISPONIBLES:', histoyVac.saldoVac)}
					{dato('SALARIO BÁSICO:', formatearMonto(salario ?? 0))}
				</Grid>
			</Paper>

			{dataLiquidacion.length === 0 ? (
				<></>
			) : (
				<Paper
					style={{
						marginTop: 10,
						padding: 20,
						textAlign: 'center'
					}}
				>
					<Typography color={'#00a152'} textAlign={'left'} fontWeight={'bold'}>
						Detalle Liquidacion
					</Typography>

					<hr />
					<TableContainer>
						<Table aria-label="simple table" size="small">
							<TableHead>
								<TableRow>
									<TableCell align="center" className="text-primary fw-bold">
										#
									</TableCell>
									<TableCell align="left" className="text-primary fw-bold">
										Detalle
									</TableCell>
									<TableCell align="center" className="text-primary fw-bold">
										Desde
									</TableCell>
									<TableCell align="center" className="text-primary fw-bold">
										Hasta
									</TableCell>
									<TableCell align="center" className="text-primary fw-bold">
										Dias
									</TableCell>
									<TableCell align="center" className="text-primary fw-bold">
										Valor
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filasMostradas.map((row, indice) => {
									const { detalle, desde, hasta, dias, valor } = row;
									return (
										<TableRow key={indice + 1}>
											<TableCell align="center" component="th" scope="row">
												{indice + 1}
											</TableCell>
											<TableCell align="left">{detalle}</TableCell>
											<TableCell align="center">
												{moment(desde).format('L')}
											</TableCell>
											<TableCell align="center">
												{moment(hasta).format('L')}
											</TableCell>
											<TableCell align="center">{dias.toFixed(2)}</TableCell>
											<TableCell align="center">
												{detalle === DETALLE_BONO ? (
													<TextField
														size="small"
														label="Bono"
														value={montoBono}
														onChange={(e) => setMontoBonoFunc(e.target.value)}
														error={Boolean(errorBono)}
														helperText={errorBono || undefined}
														inputProps={{ inputMode: 'decimal', autoComplete: 'off' }}
													/>
												) : (
													new Intl.NumberFormat('es-NI', {
														style: 'currency',
														currency: 'NIO'
													}).format(valor)
												)}
											</TableCell>
										</TableRow>
									);
								})}
								<TableRow key={0}>
									<TableCell
										align="left"
										component="th"
										scope="row"
										colSpan={5}
										style={{
											fontSize: 20,
											color: '#ffc400',
											fontWeight: 'bold'
										}}
									>
										Total
									</TableCell>
									<TableCell
										align="center"
										style={{
											fontSize: 20,
											color: '#ffc400',
											fontWeight: 'bold'
										}}
									>
										{new Intl.NumberFormat('es-NI', {
											style: 'currency',
											currency: 'NIO'
										}).format(getTotal(filasMostradas))}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			)}

			<Button
				variant="contained"
				fullWidth
				onClick={() => setConfirmando(true)}
				disabled={procesando || Boolean(errorBono)}
				sx={{ mt: 3 }}
				size="large"
			>
				Liquidar empleado
			</Button>

			<ConfirmDialog
				abierto={confirmando}
				peligro
				cargando={procesando}
				titulo="Liquidar empleado"
				mensaje={
					<>
						<Typography component="p" sx={{ mb: 1 }}>
							{`Se liquidará a ${fullName}. Se desactiva al empleado y se registra su liquidación${
								totalLiquidacion === null
									? ''
									: ` por ${formatearMonto(totalLiquidacion)}`
							}.`}
						</Typography>
						<Typography component="p" sx={{ color: 'text.secondary' }}>
							Esta acción no se puede deshacer.
						</Typography>
					</>
				}
				textoConfirmar="Liquidar empleado"
				onConfirmar={confirmarLiquidar}
				onCancelar={() => setConfirmando(false)}
			/>
		</div>
	);
};

export default LiquidateEmployee;
